import { Box, Container } from '@mui/material';
import { useMemo, useState } from 'react'
import useGlobalStyles from 'styles/globalStyles'
import PatientInformationDetails from './PatientInformationDetails';
import { useParams } from 'react-router-dom';
import { useGetProcedureById } from 'hooks/useProcedure';
import { useAuth } from 'context/AuthContext';
import { EPermission } from 'enum/EPermission';
import { ImageItem } from 'types/types/Types';
import DiagnosisDetails from './DiagnosisDetails';
import ICDDetails from './ICDDetails';
import ProcedureDetailsSection from './ProcedureDetailsSection';
import CPTDetails from './CPTDetails';
import CPTModifierDetails from './CPTModifierDetails';
import CaseProceduresDetails from './CaseProceduresDetails';
import SectionSkeleton from 'components/skeletons/SectionSkeleton';
import PageHeader from 'components/common/PageHeader/PageHeader';
import PatientOptions from 'components/case/PatientOptions/PatientOptions';
import CaseNotesDetails from 'components/case/CaseNotesDetails/CaseNotesDetails';
import CaseImagesDetails from 'components/case/CaseImagesDetails/CaseImagesDetails';
import CustomText from 'components/common/CustomText/CustomText';
import CaseNotesFab from 'components/case/CaseNotesFab/CaseNotesFab';

export default function CaseDetailsPage() {
  const { user, doctor } = useAuth();
  const { patientId } = useParams();
  const globalStyles = useGlobalStyles();
  const [procedureData, setProcedureData] = useState<any>(null);
  const [images, setImages] = useState<ImageItem[]>([]);

  const isEditor = useMemo(() => {
    return ((doctor && doctor?.permission != EPermission.VIEWER) || user?.id == procedureData?.user)
  }, [user, doctor, procedureData]);

  const { refetch, isFetching } = useGetProcedureById(Number(patientId), user.id, {
    enabled: !!patientId,
    onSuccess: (data: any) => {
      const newData = { ...data };
      setProcedureData(newData);
      setImages(prev => {
        const serverImages: ImageItem[] = newData.images ?? [];

        const serverNames = new Set(
          serverImages.map(img => (img.image_name ?? "").trim()).filter(Boolean)
        );

        const localImages = prev.filter(img => {
          if (img.id) return false;
          const name = (img.image_name ?? "").trim();
          return !(name && serverNames.has(name));
        });

        return [...serverImages, ...localImages];
      });

    }
  });

  if (!procedureData) {
    return (
      <Box className={globalStyles.container} p={3}>
        <Box >
          <PageHeader
            title={'Patient Profile'}
            buttons={[]}
          />
        </Box>
        <Box mt={3}>
          <SectionSkeleton count={9} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={4} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={7} />
        </Box>

        <Box mt={3} />
      </Box>
    )
  }

  return (
    <Box className={globalStyles.container} p={3} pb="90px">

      <Box>
        <PageHeader
          title={'Patient Profile'}
          buttons={[
            <Box key={'patient-options'}>
              {isEditor && <PatientOptions
                patientId={procedureData.id}
              />}
            </Box>
          ]}
        />
      </Box>

      <Box mt={3}>
        <PatientInformationDetails data={procedureData} />
      </Box>

      <Box mt={3}>
        <DiagnosisDetails data={procedureData} />
      </Box>

      <Box mt={3}>
        <ICDDetails data={procedureData} />
      </Box>

      <Box mt={3}>
        <ProcedureDetailsSection data={procedureData} />
      </Box>

      {procedureData.procedure_cpt.length > 0 && <Box mt={3}>
        <CPTDetails cpts={procedureData.procedure_cpt} />
      </Box>}

      {procedureData.procedure_cpt && procedureData.cpt_modifier && <Box mt={3}>
        <CPTModifierDetails cpts={procedureData.procedure_cpt} cptModifier={procedureData.cpt_modifier} />
      </Box>}

      <Box>
        <CaseProceduresDetails caseProcedures={procedureData.case_procedures} />
      </Box>

      <Box mt={3}>
        <CaseNotesDetails
          notes={procedureData.notes}
          procedureId={procedureData.id}
          refetch={refetch}
          isEditor={isEditor} />
      </Box>

      <Box mt={3}>
        <CaseImagesDetails
          images={images}
          setImages={setImages}
          procedureId={procedureData.id}
          refetch={refetch}
          isFetching={isFetching}
          isEditor={isEditor}
        />
      </Box>

      <Box mt={3} />

      <CaseNotesFab
        procedureId={procedureData.id}
        hasNotes={!!procedureData.discussion_notes_count}
        refetch={refetch}
      />

    </Box>
  )
}
